import { useEffect, useRef, useState } from 'react';

import Maybe from 'graphql/tsutils/Maybe';

import { sendPostMessage } from '~/components/shared/ProductTutorial/helpers';
import { PostMessageEvent } from '~/constants/postMessages';
import { Browser, Dictionary, Me } from '~/extension/schema';

import { usePostMessageListener } from './usePostMessageListener';

interface PostMessageData {
  type: PostMessageEvent;
  browser?: Maybe<Partial<Browser>>;
  me?: Maybe<Partial<Me>>;
  dictionary: Maybe<Partial<Dictionary>>;
}

export const useFirefoxExtension = () => {
  const hasRequestedDataRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [isListenerReady, setIsListenerReady] = useState(false);
  const [hasGlobalPermissions, setHasGlobalPermissions] = useState(false);
  const [name, setName] = useState<string>();
  const [version, setVersion] = useState<string>();
  const [userInfo, setUserInfo] = useState<Partial<Me>>();
  const [dictionary, setDictionary] = useState<Partial<Dictionary>>();

  useEffect(() => {
    if (isListenerReady && !hasRequestedDataRef.current) {
      hasRequestedDataRef.current = true;
      // TODO: Consider returning early if the browser is not Firefox
      sendPostMessage(PostMessageEvent.ExtensionInformationRequest, {});
      setLoading(true);
    }
  });

  usePostMessageListener<PostMessageData>(
    (event) => {
      const { data } = event;
      const type = data?.type;
      if (!type) {
        return;
      }
      if (type === PostMessageEvent.MessageListenerReady) {
        setIsListenerReady(true);
      }
      if (type === PostMessageEvent.ExtensionInformationResponse) {
        const { browser } = data;
        const { manifest, permissions } = browser ?? {};
        if (manifest?.name) {
          setName(manifest.name);
        }
        if (manifest?.version) {
          setVersion(manifest.version);
        }
        if (typeof permissions === 'boolean') {
          setHasGlobalPermissions(permissions);
        }
        if (data.me) {
          setUserInfo(data.me);
        }
        if (data.dictionary) {
          setDictionary(data.dictionary);
        }
        setLoading(false);
      }
    },
    [setIsListenerReady, setHasGlobalPermissions, setLoading],
  );
  return {
    dictionary,
    isInstalledInFirefox: isListenerReady,
    hasGlobalPermissionsInFirefox: !!hasGlobalPermissions,
    loading,
    name,
    refetch: () => {
      sendPostMessage(PostMessageEvent.ExtensionInformationRequest, {});
    },
    userInfo,
    version,
  };
};
